"use client";
import { useState, useMemo, memo } from "react";
import Image, { ImageProps } from "next/image";
import useFallbackImage from "@/app/hooks/useFallbackImage";
import { encodeImageUrl } from "@/app/utils/encodeImageUrl";

interface FallbackImageProps extends Omit<ImageProps, "onError"> {
  fallbackClassName?: string;
}

/**
 * Ürün görsellerinin CDN'i. Bu host Next optimizer'ını BİLEREK atlıyor.
 *
 * cdn.tozlu.com URL'leri zaten `/img/{genişlik}/{kalite}/…webp` biçiminde —
 * yani boyutlandırma ve webp dönüşümü CDN'de yapılmış durumda. Bunları
 * /_next/image üzerinden geçirmek, katalogdaki ~65 bin ürün görselini tek Node
 * sürecinde genişlik × format kombinasyonları için yeniden encode edip diske
 * yazmak demek: kazanç marjinal, CPU ve disk maliyeti gerçek. Optimizasyonun
 * ölçülen 4 MB'lık kazancı API origin'indeki statik varlıklarda (512×512 story
 * PNG'leri, kategori ikonları, logo) — onlar optimizer'dan geçmeye devam ediyor.
 *
 * Ürün görsellerini de optimize etmek istersen bu sabiti boşaltmak yeterli;
 * öncesinde sunucu CPU'sunu ve disk cache boyutunu izlemek şart.
 */
const CDN_HOST = (process.env.NEXT_PUBLIC_CDN_URL || "https://cdn.tozlu.com")
  .replace(/^https?:\/\//, "")
  .replace(/\/+$/, "");

const isValidSrc = (src: ImageProps["src"]): boolean => {
  if (!src) return false;
  if (typeof src === "string") return src.length > 0;
  return true;
};

// Add key to component name to force remount when src/fallbackLogo changes
const FallbackImageInner = ({
  src,
  alt,
  fallbackClassName = "object-contain opacity-60",
  className,
  ...props
}: FallbackImageProps) => {
  const { fallbackLogo, fallbackAlt, DEFAULT_LOGO } = useFallbackImage();
  const validSrc = isValidSrc(src);
  const [loadError, setLoadError] = useState(false);
  const [fallbackLoadError, setFallbackLoadError] = useState(false);

  const errorLevel = useMemo(() => {
    if (!validSrc || loadError) {
      return fallbackLoadError ? 2 : 1;
    }
    return 0;
  }, [validSrc, loadError, fallbackLoadError]);

  const rawResolvedSrc =
    errorLevel === 0 ? src : errorLevel === 1 ? fallbackLogo : DEFAULT_LOGO;
  // String URL'leri encode et. Gerekçe: optimizer'ı atlayan görsellerde
  // (CDN_HOST) priority preload'ı ham URL ile Link header'ına yazılıyor ve
  // Türkçe karakter ByteString hatası veriyor. Optimizer'dan geçenler için
  // Next URL'i zaten encode ediyor — bu durumda zararsız.
  const resolvedSrc =
    typeof rawResolvedSrc === "string"
      ? encodeImageUrl(rawResolvedSrc)
      : rawResolvedSrc;
  const resolvedAlt = errorLevel === 0 ? alt : fallbackAlt;
  const resolvedClassName = errorLevel === 0 ? className : fallbackClassName;

  const handleError = () => {
    if (errorLevel === 0) {
      setLoadError(true);
    } else if (errorLevel === 1) {
      setFallbackLoadError(true);
    }
  };

  const imageKey =
    typeof resolvedSrc === "string"
      ? `${resolvedSrc}-${errorLevel}`
      : `static-${errorLevel}`;

  // Çağıran açıkça bir değer geçtiyse ona saygı gösterilir; yoksa CDN host'u
  // optimizer'ı atlar (bkz. CDN_HOST).
  const skipOptimizer =
    props.unoptimized ??
    (typeof resolvedSrc === "string" && resolvedSrc.includes(CDN_HOST));

  return (
    <Image
      {...props}
      quality={props.quality ?? 90}
      src={resolvedSrc}
      alt={resolvedAlt}
      className={resolvedClassName}
      onError={handleError}
      unoptimized={skipOptimizer}
      key={imageKey}
    />
  );
};

// Wrapper component that uses key to reset state when src changes
const FallbackImage = memo((props: FallbackImageProps) => {
  return <FallbackImageInner key={`${props.src}`} {...props} />;
});

FallbackImage.displayName = "FallbackImage";

export default FallbackImage;
